/* eslint-disable no-unused-vars */

import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import MicOffIcon from '@mui/icons-material/MicOff';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { Logger } from '../../../common/Logger';
import { user } from '../../../recoil/atoms/user';

// eslint-disable-next-line no-unused-vars
const useStyles = makeStyles((theme) => ({
  button: {
    marginBottom: theme.spacing(1),
    color: 'white',
    '& .MuiButton-label': {
      padding: 2,
    },
  },
  spacing: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  red: {
    backgroundColor: 'red',
    '&:hover': {
      backgroundColor: 'darkred',
    },
  },
  audio: {
    transition: 'all 0.5s linear',
    boxShadow: '0px 0px 0px 0px',
    backgroundColor: '#f0f3f4',
    borderRadius: '7px 7px 7px 7px',
    height: 32,
    maxWidth: '-webkit-fill-available',
    margin: '0px auto',
    '&:focus': {
      outline: 0,
    },
  },
}));

/**
 * @callback setFileToUploadCallback
 * @param {File} file File that contains recorded audio
 */

/**
 * Recorder component
 *
 * @param {Object} props
 * @param {string} [props.className] Classname to apply to container
 * @param {setFileToUploadCallback} [props.setFileToUpload] Callback use to set the file
 *
 * @see https://github.com/muaz-khan/RecordRTC/issues/351
 * @see https://github.com/muaz-khan/WebRTC-Experiment/blob/master/RecordRTC/simple-demos/audio-recording.html
 * @see https://github.com/szwang/recordrtc-react/blob/master/src/RecordPage.react.js
 * @see https://suzannewang.com/recordrtc/
 * @see https://www.webrtc-experiment.com/RecordRTC/simple-demos/audio-recording.html
 * @see https://flashphoner.com/ios-safari-11-now-supports-webrtc/
 * @see https://www.npmjs.com/package/react-media-recorder
 * @see https://www.npmjs.com/package/react-mic
 *
 * @returns {JSX.Element}
 */
export const WebRTCRecorder = ({
  className = undefined,
  setFileToUpload = null,
}) => {
  const classes = useStyles();
  const isEdge =
    navigator.userAgent.includes('Edge') &&
    (!!navigator.msSaveOrOpenBlob || !!navigator.msSaveBlob);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const [disabledStartRecording, setDisabledStartRecording] = React.useState(
    false
  );
  const [disabledStopRecording, setDisabledStopRecording] = React.useState(
    true
  );
  const [
    disabledReleaseMicrophone,
    setDisabledReleaseMicrophone,
  ] = React.useState(true);
  const [
    disabledDownloadRecording,
    setDisabledDownloadRecording,
  ] = React.useState(true);
  const [disabledAudioPlayback] = React.useState(true);
  const [audioState, setAudioState] = React.useState({
    paused: false,
    play: false,
    muted: false,
    recorder: null,
    src: null,
    mimeType: 'audio/webm', // Default to WebM for server compatibility
    downloadFilename: null,
  });
  const audioRef = useRef(null);
  const userAtom = useRecoilValueLoadable(user);
  const loginUser = userAtom.contents?.user ?? {};

  /**
   * Call setFileToUpload callback with file converted from blob
   * @param {object} audioState - Local state
   * @param {function} getFileName - function that returns generated filename
   */
  const convertBlobToFileCallback = (audioState, getFileName) => {
    if (setFileToUpload) {
      const ftu = new File([window.recordingBlob], getFileName(), { type: audioState.mimeType });
      setFileToUpload(ftu);
    }
  };

  function visualize(stream) {
    const canvas = audioRef.current;
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const canvasCtx = canvas.getContext('2d');
    const source = audioCtx.createMediaStreamSource(stream);

    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);
    // analyser.connect(audioCtx.destination);

    draw();

    function draw() {
      requestAnimationFrame(draw);

      analyser.getByteTimeDomainData(dataArray);

      canvasCtx.fillStyle = 'rgb(200, 200, 200)';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = 'rgb(0, 0, 0)';

      canvasCtx.beginPath();

      const sliceWidth = (canvas.width * 1.0) / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();
    }
  }

  function captureMicrophone(callback) {
    if (audioState.microphone) {
      callback(audioState.microphone);
      return;
    }

    if (
      typeof navigator.mediaDevices === 'undefined' ||
      !navigator.mediaDevices.getUserMedia ||
      isSafari
    ) {
      if (isSafari) {
        // volume level will be set too low on Safari
        alert('This browser is not supported. Please use Google Chrome.');
      } else {
        alert('This browser does not supports WebRTC getUserMedia API.');
      }

      if (navigator.getUserMedia) {
        alert('This browser seems supporting deprecated getUserMedia API.');
      }

      // exit of webrtc not supported
      return;
    }

    setDisabledReleaseMicrophone(false);

    navigator.mediaDevices
      .getUserMedia({
        audio: isEdge
          ? true
          : {
              echoCancellation: true,
            },
      })
      .then(function (stream) {
        if (stream) {
          window.chunks = [];
          window.recordingURL = null;
          window.recordingBlob = null;

          // Set MIME type for MediaRecorder - keep as WebM for server compatibility
          let mimeType = 'audio/webm';
          if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            mimeType = 'audio/webm;codecs=opus';
          } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            mimeType = 'audio/webm';
          }

          window.mediaRecorder = new MediaRecorder(stream, { mimeType });
          window.mediaRecorder.ondataavailable = function (evt) {
            // push each chunk (blobs) in an array
            window.chunks.push(evt.data);
          };
          window.mediaRecorder.onstop = function (evt) {
            // Create blob with correct MIME type
            window.recordingBlob = new Blob(window.chunks, { type: mimeType });
            window.recordingURL = URL.createObjectURL(window.recordingBlob);
            replaceAudio(window.recordingURL, mimeType);
          };

          // Store mimeType in state for later use
          setAudioState((state) => ({
            ...state,
            mimeType,
          }));
          visualize(stream);
          callback(stream);
        }
      })
      .catch(function (error) {
        alert('Unable to capture your microphone. Please check console logs.');
        // eslint-disable-next-line autofix/no-console, no-console
        console.error(error);
      });
  }

  function replaceAudio(src, mimeType = 'audio/webm') {
    if (src) {
      const fileExtension = getFileExtensionFromMimeType(mimeType);
      const downloadFilename = getFileName(fileExtension);

      const newState = {
        // destructure since setAudioState executes asynchronously
        ...audioState,
        src,
        mimeType,
        downloadFilename,
      };
      setAudioState(newState);
      convertBlobToFileCallback(newState, () => downloadFilename);
    }
  }

  function stopRecordingCallback() {
    setDisabledStartRecording(false);

    setTimeout(function () {
      if (!audioState.paused) {
        return;
      }

      setTimeout(function () {
        if (!audioState.paused) {
          return;
        }
        setAudioState((state) => ({
          ...state,
          play: true,
        }));
      }, 1000);

      setAudioState((state) => ({
        ...state,
        play: true,
      }));
    }, 300);

    setAudioState((state) => ({
      ...state,
      play: true,
    }));

    setDisabledDownloadRecording(false);

    if (isSafari) {
      btnReleaseMicrophone();
    }
  }

  // function click(el) {
  //   el.disabled = false; // make sure that element is not disabled
  //   var evt = document.createEvent('Event');
  //   evt.initEvent('click', true, true);
  //   el.dispatchEvent(evt);
  // }

  function getRandomString() {
    if (
      window?.crypto?.getRandomValues &&
      !navigator.userAgent.includes('Safari')
    ) {
      const a = window.crypto.getRandomValues(new Uint32Array(3));
      let token = '';
      for (let i = 0, l = a.length; i < l; i++) {
        token += a[i].toString(36);
      }
      return token;
    } else {
      return (Math.random() * new Date().getTime())
        .toString(36)
        .replace(/\./g, '');
    }
  }

  function getFileName(fileExtension) {
    const d = new Date();
    const year = d.getFullYear();
    const month = d.getMonth();
    const date = d.getDate();
    return (
      'RecordRTC-' +
      year +
      month +
      date +
      '-' +
      getRandomString() +
      '.' +
      fileExtension
    );
  }

  function getFileExtensionFromMimeType(mimeType) {
    // Only WebM formats are supported for server compatibility
    return 'webm';
  }

  const btnStartRecording = (_event, microphone) => {
    setDisabledStartRecording(true);
    // this.style.border = '';
    // this.style.fontSize = '';
    microphone = microphone || audioState.microphone;

    if (!microphone) {
      captureMicrophone(function (mic) {
        setAudioState((state) => ({
          ...state,
          microphone: mic,
        }));

        if (isSafari) {
          replaceAudio(null, audioState.mimeType);

          setAudioState((state) => ({
            ...state,
            muted: true,
          }));
          audioRef.current.setAttribute('style', 'border: 1px solid red');

          setDisabledStartRecording(false);
          // btnStartRecording.style.border = '1px solid red';
          // btnStartRecording.style.fontSize = '150%';

          Logger.trace(
            'Please click startRecording button again. First time we tried to access your microphone. Now we will record it.'
          );
          return;
        }

        btnStartRecording(_event, mic);
      });
      return;
    }

    replaceAudio(null, audioState.mimeType);

    setAudioState((state) => ({
      ...state,
      muted: true,
    }));

    const options = {
      type: 'audio',
      numberOfAudioChannels: isEdge ? 1 : 2,
      checkForInactiveTracks: true,
      bufferSize: 16384,
      recorderType: StereoAudioRecorder, // needed for isSafari/isEdge and makes download to work
    };

    if (
      navigator.platform &&
      !navigator.platform.toString().toLowerCase().includes('win')
    ) {
      options.sampleRate = 48000; // or 44100 or remove this line for default
    }

    if (isSafari) {
      options.sampleRate = 44100;
      options.bufferSize = 4096;
      options.numberOfAudioChannels = 2;
    }

    if (audioState.recorder) {
      audioState.recorder.destroy();
      setAudioState((state) => ({
        ...state,
        recorder: null,
      }));
    }

    const recorder = RecordRTC(microphone, options);
    setAudioState((state) => ({
      ...state,
      recorder,
    }));

    // recorder.startRecording();
    window.mediaRecorder.start();

    setDisabledStopRecording(false);
    setDisabledDownloadRecording(true);
  };

  const btnStopAndReleaseRecording = () => {
    btnStopRecording();
    btnReleaseMicrophone();
  };

  const btnStopRecording = () => {
    setDisabledStopRecording(true);

    window.mediaRecorder.stop();
    stopRecordingCallback();
  };

  const btnReleaseMicrophone = () => {
    setDisabledReleaseMicrophone(true);
    setDisabledStartRecording(false);

    if (audioState.microphone) {
      audioState.microphone.stop();
      setAudioState((state) => ({
        ...state,
        microphone: null,
      }));
    }
  };

  return (
    <div className={className}>
      <Box display="flex" justifyContent="center">
        <Button
          data-test-id="btnStartRecording"
          onClick={() => {
            btnStartRecording();
          }}
          hidden={disabledStartRecording}
          className={`${classes.button} ${classes.spacing}`}
          color="primary"
          startIcon={<KeyboardVoiceIcon />}
          variant="contained"
        >
          Start Recording
        </Button>
        <Button
          data-test-id="btnStopRecording"
          onClick={btnStopAndReleaseRecording}
          hidden={disabledStopRecording}
          className={`${classes.button} ${classes.red} ${classes.spacing}`}
          startIcon={<MicOffIcon />}
          variant="contained"
        >
          Stop Recording
        </Button>
        {/* <Button
          data-test-id="btnReleaseMicrophone"
          onClick={btnReleaseMicrophone}
          hidden={disabledReleaseMicrophone}
          className={`${classes.button} ${classes.red} ${classes.spacing}`}
          startIcon={<MicOffIcon />}
          variant="contained"
        >
          Release Microphone
        </Button>
        <Button
          data-test-id="btnDownloadRecording"
          onClick={btnDownloadRecording}
          hidden={disabledDownloadRecording}
          className={`${classes.button} ${classes.red} ${classes.spacing}`}
          startIcon={<MicOffIcon />}
          variant="contained"
        >
          Download
        </Button> */}
      </Box>
      <canvas
        className={`visualizer ${classes.canvas} ${classes.spacing}`}
        hidden={disabledStopRecording && disabledReleaseMicrophone}
        height="60"
        ref={audioRef}
      ></canvas>
      <audio
        data-test-id="cmpAudioPlayback"
        className={clsx(classes.spacing, classes.audio)}
        hidden={disabledDownloadRecording}
        controls
        autoPlay
        playsInline
        disabled={disabledAudioPlayback}
        src={audioState.src}
      ></audio>
    </div>
  );
};
